// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ExternalLink } from '@/components/external-link';
import {
  ShippingCarrier,
  getTrackingLabelForShippingCarrier,
  getTrackingURLForShippingCarrier,
} from '@/constants/shipping-carrier';
import { Typography, TypographyProps } from '@mui/material';

/**
 * Returns the tracking URL for a shipment.
 *
 * @param carrier - The carrier of the shipment.
 * @param trackingIdentifier - The tracking identifier of the shipment.
 */
function getURL(
  carrier: ShippingCarrier | null,
  trackingIdentifier: string | null
) {
  if (carrier === null || trackingIdentifier === null) {
    return null;
  }

  return getTrackingURLForShippingCarrier(carrier, trackingIdentifier);
}

interface Props extends TypographyProps {
  /**
   * The carrier of the shipment.
   */
  carrier: ShippingCarrier | null;

  /**
   * The tracking identifier of the shipment.
   */
  trackingIdentifier: string | null;

  /**
   * Whether to link the tracking number directly or show a "Track" button.
   */
  linkNumberDirectly?: boolean;
}

/**
 * A label that renders a carrier name and tracking identifier. For supported
 * carriers, a link to the tracking page is also included.
 */
export function ShippingCarrierTrackingLabel(props: Props) {
  const {
    carrier,
    trackingIdentifier,
    linkNumberDirectly = false,
    ...typographyProps
  } = props;

  const url = getURL(carrier, trackingIdentifier);
  const label = getTrackingLabelForShippingCarrier(carrier, trackingIdentifier);

  if (label === null) {
    return null;
  }

  return (
    <Typography variant="caption" {...typographyProps}>
      {url && linkNumberDirectly ? (
        <ExternalLink href={url}>{label}</ExternalLink>
      ) : (
        <>
          {label} &nbsp;
          {url && <ExternalLink href={url}>Track</ExternalLink>}
        </>
      )}
    </Typography>
  );
}
